import { Constructor } from '@skylab-shared/types';
import { head } from '@skylab-shared/utils';
import { Weaver } from './../../aop';
import { BusinessObjectBase } from './../../bo';
import { ModelBase } from './../../model';
import { CollectionChangingAction, ObservableCollection } from './../../observable';
import { ValidationResult, ValidationRule, ValidationSource, Validator, Violation } from './../../validation';
import { AttachedRule } from './attached-rule';
import { CollectionChangedJoinPoint } from './joinpoints/collection-changed';
import { PropertyChangedJoinPoint } from './joinpoints/property-changed';
import { RuleDefinition, RuleResult, RulesEngine } from './rules-engine';


type ValidationRuleInternalInfo = { external?: true; };
type ValidationRuleResult = ValidationResult<ModelBase> & ValidationRuleInternalInfo;

export class ValidationEngine<TBo extends BusinessObjectBase<TModel>, TModel extends ModelBase>
    extends RulesEngine<TBo, TModel, ValidationRuleResult, Constructor<Violation>>
    implements ValidationSource<ModelBase> {

    private readonly validator: Validator = new Validator();

    public constructor(weaver: Weaver) {
        super(weaver);
        this.registerExternalValidationsObservers();
    }

    public get validationResult(): ObservableCollection<ValidationRuleResult> {
        return this.result;
    }

    public addExternalValidationResult(validationResult: ValidationResult<ModelBase>, id: string): void {
        this.result.push({ id: id, external: true, ...validationResult });
    }

    public removeExternalValidationResults(id: string): void {
        this.result.remove(...this.result.filter(r => r.id === id && r.external === true));
    }

    protected override createAttachedRule(ruleDefinition: RuleDefinition<Constructor<Violation>>, triggeredByModel: ModelBase, effectOnModel: ModelBase): AttachedRule<ValidationResult<ModelBase> & RuleResult, Constructor<Violation>> {
        return new AttachedValidationRule(this.validator, ruleDefinition, this.bo, triggeredByModel, effectOnModel);
    }

    public queueValidationByViolationType(type: Constructor<Violation>): void {
        const rules = this.attachedRules.filter(attachedRule => attachedRule.args === type);
        this.queueAttachedRulesPerformation(rules);
    }

    private registerExternalValidationsObservers(): void {
        this.registerListening(this.weaver.register(
            PropertyChangedJoinPoint,
            ctx => this.result.remove(...this.result.filter(r => r.external === true && r.source === ctx.model && r.propertyName === ctx.propertyName))
        ));

        this.registerListening(this.weaver.register(
            CollectionChangedJoinPoint,
            ctx => {
                if (ctx.action === CollectionChangingAction.Add) return;
                const removedModels = ctx.oldItems.filter(item => !ctx.newItems.includes(item));
                this.result.remove(...this.result.filter(r => r.external === true && removedModels.includes(r.source)));
            }
        ));
    }
}

export class AttachedValidationRule extends AttachedRule<ValidationResult<ModelBase> & RuleResult, Constructor<Violation>> {
    private readonly validator: Validator;
    private readonly validationRule: ValidationRule;

    public constructor(validator: Validator,
        ruleDefinition: RuleDefinition<Constructor<Violation>>,
        bo: BusinessObjectBase<ModelBase>,
        source: ModelBase,
        target: ModelBase) {
        super(ruleDefinition, bo, source, target);

        this.validator = validator;
        this.validationRule = {
            async: true,
            validateAsync: this.ruleDefinition.rule.funcAsync,
            violationType: this.ruleDefinition.args
        };
    }

    protected override async validateRule(): Promise<ValidationResult<ModelBase> & RuleResult> {
        const ctx = { bo: this.bo, source: this.source, target: this.target };
        const violations = await this.validator.validateAsync(ctx, [[this.validationRule, void 0]]);
        const violation = head(violations);
        return violation ? {
            source: this.target,
            propertyName: null as any,
            ruleViolation: violation,
            id: this.ruleId
        } : null as any;
    }
}
